import React from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRouteMatch, Link as RouterLink, useHistory } from 'react-router-dom';
import { ListItem, ListItemIcon, ListItemText, Drawer } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch } from 'react-redux';
import { clearCredentials, selectAuthState } from '../../shared/sagas/auth/authSlice';
import DarkProfileSettingsIcon from '../../icons/DarkProfileSettingsIcon.svg';
import WhiteInosio from '../../icons/whiteinosio.svg';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { selectPropertyGroupDetailsState } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { makeStyles } from '@mui/styles';
import { format, subMonths } from 'date-fns';

const useStyles = makeStyles({
  LogoContainer: {
    width: '226px',
    height: '74px',
    left: '20px',
    display: 'flex',
    color: '#14142B',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '9px',
    marginBottom: '103px',
    '@media (max-width: 600px)': {
      marginBottom: '49px',
    },
  },
  menuContainer: {
    display: 'flex',
    flexDirection: 'column',
    color: '#14142B',
    justifyContent: 'space-between',
    alignItems: 'space-between',
    height: '75%',
  },
  listItem: {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '22px',
    color: '#14142B',
    textDecoration: 'none',
    width: '100%',
    height: '100%',
  },
  activeListItem: {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: '22px',
    color: '#14142B',
    textDecoration: 'none',
  },
});

interface LeftLinkProps {
  Text: string;
  Icon: string;
  path: string;
  selected?: boolean;
}

const drawerWidth: number = 290;

const DesktopDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      // overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(0),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(0),
      },
    }),
  },
}));

interface LeftDrawerPanelProps {
  open: boolean;
  setOpen: (_: boolean) => void;
  toggleMobileDrawer: (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => void;
  state: any;
}

type Anchor = 'left';

export default function LeftDrawerPanel({ open, setOpen, state, toggleMobileDrawer }: LeftDrawerPanelProps) {
  const classes = useStyles();
  let { url } = useRouteMatch();
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const matchesIpad = useMediaQuery('(max-width:1024px)');
  const history = useHistory();
  const dispatch = useDispatch();
  const { propertyGroupDetails: PropertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const { ipaUser } = useSelector(selectAuthState);
  const [linksOnLeft, setLinksOnLeft] = React.useState<LeftLinkProps[]>([]);
  const pathName = window.location.pathname;

  //on initial load of the ledger component we wanna show this date range of ledger activity
  const defaultLedgerActivityStartDate = format(subMonths(new Date(), 2), 'yyyy-MM-dd');
  const defaultLedgerActivityEndDate = format(new Date(), 'yyyy-MM-dd');

  const handleLogout = () => {
    dispatch(clearCredentials());
    history.push('/login');
  };

  React.useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      setLinksOnLeft([
        {
          Text: 'Ledger',
          Icon: DarkProfileSettingsIcon,
          path: `${url}/ledger?startDate=${defaultLedgerActivityStartDate}&endDate=${defaultLedgerActivityEndDate}`,
        },
        {
          Text: 'Documents',
          Icon: DarkProfileSettingsIcon,
          path: `${url}/documents`,
        },
        {
          Text: 'Profile Settings',
          Icon: DarkProfileSettingsIcon,
          path: `${url}/profilesettings`,
        },
      ]);
    }
  }, [ipaUser, PropertyGroupDetails]);

  const list = (anchor: Anchor) => (
    <Box sx={{ width: 290 }} role="presentation" onClick={toggleMobileDrawer(anchor, false)} onKeyDown={toggleMobileDrawer(anchor, false)} style={{ zIndex: 999 }}>
      <div className={classes.menuContainer}>
        <List>
          {linksOnLeft.map((link) => (
            <ListItem button key={link.Text} selected={link.path === pathName}>
              <RouterLink to={link.path} id="navLink" className={link.path === pathName ? classes.activeListItem : classes.listItem}>
                <IconButton>
                  <img
                    src={link.Icon}
                    style={{
                      width: link.path === pathName ? '35px' : '25px',
                      height: link.path === pathName ? '29px' : '25px',
                    }}
                    alt="action"
                  />
                </IconButton>
                {link.Text}
              </RouterLink>
            </ListItem>
          ))}
        </List>
      </div>
    </Box>
  );

  return (
    <>
      {' '}
      {!matchesIpad ? (
        <DesktopDrawer variant="permanent" open={open} onClose={() => setOpen(!open)} anchor="left">
          <div className={classes.LogoContainer}>
            <IconButton>
              <img
                src={matchesMobile ? WhiteInosio : WhiteInosio}
                style={{
                  width: matchesMobile ? '58px' : '74px',
                  height: matchesMobile ? '58px' : '74px',
                }}
                alt="action"
              />
            </IconButton>
            {matchesIpad ? (
              <Toolbar
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: [1],
                }}
              >
                <IconButton onClick={() => setOpen(!open)}>
                  <ChevronLeftIcon />
                </IconButton>
              </Toolbar>
            ) : (
              ''
            )}
          </div>
          <div className={classes.menuContainer}>
            <List>
              {linksOnLeft.map((link) => (
                <ListItem button key={link.Text} selected={link.path === pathName}>
                  <RouterLink to={link.path} id="navLink" className={link.path === pathName ? classes.activeListItem : classes.listItem}>
                    <IconButton>
                      <img
                        src={link.Icon}
                        style={{
                          width: link.path === pathName ? '35px' : '25px',
                          height: link.path === pathName ? '29px' : '25px',
                        }}
                        alt="action"
                      />
                    </IconButton>
                    {link.Text}
                  </RouterLink>
                </ListItem>
              ))}
            </List>
            {!matchesMobile ? (
              <List>
                <ListItem button key="logout" onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Log out" />
                </ListItem>
              </List>
            ) : (
              ''
            )}
          </div>
        </DesktopDrawer>
      ) : (
        <div>
          {(['left'] as const).map((anchor) => (
            <React.Fragment key={anchor}>
              <Drawer anchor="left" open={state['left']} onClose={toggleMobileDrawer('left', false)}>
                <div className={classes.LogoContainer}>
                  <IconButton>
                    <img
                      src={matchesMobile ? WhiteInosio : WhiteInosio}
                      style={{
                        width: matchesMobile ? '58px' : '74px',
                        height: matchesMobile ? '58px' : '74px',
                      }}
                      alt="action"
                    />
                  </IconButton>
                  <Typography id="logoName">PropertyName...</Typography>
                  {matchesIpad ? (
                    <Toolbar
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: [1],
                      }}
                    />
                  ) : (
                    ''
                  )}
                </div>
                {list(anchor)}
              </Drawer>
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}
